import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { blogArticles } from "./blogArticles";

const BLOG_URL = "https://meshum.com/blog";
const SEARCH_LINKS = [
  {
    to: "/search",
    title: "Search used cars on Meshum",
    description: "Compare active listings with local pricing context and sold comparable vehicles."
  },
  {
    to: "/search?zip=33131&radius=50",
    title: "Browse used cars near Miami",
    description: "Review South Florida inventory around Miami before comparing local market value."
  },
  {
    to: "/used-cars/toyota/camry",
    title: "Toyota Camry inventory and market context",
    description: "See current Camry listings and compare them with pricing guides about trim, mileage, and value."
  }
];

function BlogIndex() {
  const latestArticles = [...blogArticles]
    .sort((a, b) => new Date(`${b.publishDate}T00:00:00`) - new Date(`${a.publishDate}T00:00:00`))
    .slice(0, 3);

  const featuredArticles = blogArticles.filter((article) =>
    [
      "used-hybrid-ev-prices-in-miami",
      "best-time-to-buy-a-used-car-in-miami",
      "how-to-know-if-a-used-car-is-overpriced"
    ].includes(article.slug)
  );

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Meshum Blog",
    description: "Car-shopping guides about sold comparables, pricing, negotiation, and market value.",
    url: BLOG_URL,
    blogPost: blogArticles.map((article) => ({
      "@type": "BlogPosting",
      headline: article.title,
      description: article.description,
      datePublished: article.publishDate,
      url: article.canonicalUrl
    }))
  };

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-10 sm:px-6 lg:px-8">
      <Helmet>
        <title>Meshum Blog | Used Car Pricing Guides</title>
        <meta
          name="description"
          content="Simple used-car pricing guides from Meshum covering sold comparables, asking prices, market value, and negotiation."
        />
        <link rel="canonical" href={BLOG_URL} />
        <meta property="og:title" content="Meshum Blog | Used Car Pricing Guides" />
        <meta
          property="og:description"
          content="Learn how sold vehicle comparables and market data help shoppers understand whether a car is priced fairly."
        />
        <meta property="og:url" content={BLOG_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://meshum.com/share-thumbnail.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Meshum Blog | Used Car Pricing Guides" />
        <meta
          name="twitter:description"
          content="Used-car pricing guides about sold comparables, local market trends, depreciation, financing, and vehicle value."
        />
        <meta name="twitter:image" content="https://meshum.com/share-thumbnail.jpg" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <section className="mx-auto max-w-5xl">
        <div className="mb-8">
          <div className="inline-flex rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">
            Meshum Blog
          </div>
          <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
            Used car pricing guides
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-7 text-slate-600">
            Meshum publishes practical used-car pricing guides for shoppers who want to understand market value before they buy, sell, finance, or negotiate. These articles explain how sold comparable vehicles, active listings, local market conditions, depreciation, and the Meshum Estimate can help consumers judge whether a car is priced fairly.
          </p>
        </div>

        <section className="mb-10">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-950">Featured pricing guides</h2>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {featuredArticles.map((article) => (
              <article key={article.slug} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-xs font-semibold uppercase text-blue-700">Featured</div>
                <h3 className="mt-2 text-xl font-semibold leading-snug text-slate-950">
                  <Link to={`/blog/${article.slug}`} className="text-slate-950 no-underline hover:text-blue-700">
                    {article.title}
                  </Link>
                </h3>
                <p className="mt-3 text-sm leading-6 text-slate-600">{article.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-950">Latest articles</h2>
          <div className="mt-4 grid gap-4">
            {latestArticles.map((article) => (
              <article key={article.slug} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-sm font-medium text-slate-500">
                  {new Date(`${article.publishDate}T00:00:00`).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric"
                  })}
                </div>
                <h3 className="mt-2 text-xl font-semibold tracking-tight text-slate-950">
                  <Link to={`/blog/${article.slug}`} className="text-slate-950 no-underline hover:text-blue-700">
                    {article.title}
                  </Link>
                </h3>
                <p className="mt-3 text-sm leading-6 text-slate-600">{article.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-950">Compare real listings</h2>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
            Use these guides with live Meshum inventory so pricing advice connects back to actual vehicles, nearby alternatives, and current market context.
          </p>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {SEARCH_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="rounded-xl border border-slate-200 bg-white p-5 text-slate-950 no-underline shadow-sm hover:border-blue-300"
              >
                <span className="block text-base font-semibold text-slate-950">{link.title}</span>
                <span className="mt-2 block text-sm leading-6 text-slate-600">{link.description}</span>
              </Link>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold tracking-tight text-slate-950">All used-car pricing articles</h2>
          <div className="mt-4 grid gap-4">
          {blogArticles.map((article) => (
            <article key={article.slug} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="text-sm font-medium text-slate-500">
                {new Date(`${article.publishDate}T00:00:00`).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric"
                })}
              </div>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">
                <Link to={`/blog/${article.slug}`} className="text-slate-950 no-underline hover:text-blue-700">
                  {article.title}
                </Link>
              </h2>
              <p className="mt-3 text-sm leading-6 text-slate-600">{article.description}</p>
              <Link
                to={`/blog/${article.slug}`}
                className="mt-5 inline-flex text-sm font-semibold text-blue-700 underline decoration-blue-300 underline-offset-4 hover:text-blue-800"
              >
                Read article
              </Link>
            </article>
          ))}
          </div>
        </section>
      </section>
    </main>
  );
}

export default BlogIndex;
